import { ContextMenuButton, Td, Tr } from 'app/components/Table';
import { MouseEvent } from 'react';
import { Row } from '@tanstack/react-table';
import { ListItem, ListItemText } from 'app/components/ChakraTableList';
import {
  GroupedVersionCredits,
  ReleaseType,
  ReleaseVersionContribution,
  ValidationError,
} from 'types/releases';
import { ReactComponent as LineIcon } from 'resources/images/line.svg';
import { format } from 'date-fns';
import { dateFormats } from 'app/constants';
import { Checkbox, Spinner, Tag, TagLabel, chakra } from '@chakra-ui/react';
import { GenericObject } from 'types/generic.types';
import { getErrorMsg, getSubheaderProps } from './utils';
import { CreditListSubheader } from '../CreditListSubheader';
import { ValidationTooltip } from './ValidationTooltip';

interface AudioTableRowProps
  extends Partial<ReturnType<typeof getSubheaderProps>> {
  row: Row<GroupedVersionCredits>;
  validationErrors?: ValidationError[];
  isUpdating: GenericObject<boolean>;
  releaseType: ReleaseType;
  contextHandler: (e: MouseEvent<HTMLDivElement | HTMLButtonElement>) => void;
  onCreditedChange: (contribution: ReleaseVersionContribution) => Promise<void>;
}

export const ContributionRow = ({
  row,
  validationErrors,
  contextHandler,
  isUpdating,
  onCreditedChange,
  releaseType,
  addedByColumn,
  date,
  location,
  roleColumn,
}: AudioTableRowProps) => {
  const credit = row.original;
  const { parentId } = row;
  if (parentId === undefined) {
    return (
      <Tr
        zIndex={1}
        position="relative"
        role="row"
        data-group
        sx={{
          _hover: {},
          bg: 'transparent !important',
        }}
      >
        <Td sx={{ overflow: 'visible' }}>
          <CreditListSubheader credit={credit} />
        </Td>
        <Td />
        <Td />
        <Td />
        <Td />
        <Td />
        <Td />
        <Td />
      </Tr>
    );
  }

  const contribution = (row.original as unknown) as ReleaseVersionContribution;
  const errorMsg = getErrorMsg(contribution, validationErrors);

  return (
    <Tr
      zIndex={1}
      position="relative"
      role="row"
      data-group
      even
      className={row.index % 2 === 0 ? 'even' : 'odd'}
      sx={{ _even: {}, _hover: { zIndex: 2 } }}
      onContextMenu={contextHandler}
    >
      <Td>
        <Tag
          key={roleColumn}
          data-testid={`${roleColumn}-chip`}
          title={roleColumn}
          textDecoration={contribution.isExcluded ? 'line-through' : 'none'}
        >
          <TagLabel>{roleColumn}</TagLabel>
        </Tag>
      </Td>
      <Td>
        <ListItemText
          data-testid="date"
          primary={
            date?.date ? (
              format(new Date(date.date), dateFormats.LIST_FORMAT)
            ) : (
              <LineIcon width="24px" />
            )
          }
          primaryTextProps={{
            textDecoration: contribution.isExcluded ? 'line-through' : 'none',
          }}
        />
      </Td>
      <Td>
        <ListItemText
          data-testid="country"
          primary={location?.country?.countryCode || <LineIcon width="24px" />}
          primaryTextProps={{
            textDecoration: contribution.isExcluded ? 'line-through' : 'none',
          }}
        />
      </Td>
      <Td>
        <ListItemText
          data-testid="created-by"
          primary={addedByColumn}
          title={addedByColumn}
          primaryTextProps={{
            textDecoration: contribution.isExcluded ? 'line-through' : 'none',
          }}
        />
      </Td>
      <Td>
        <ListItemText
          data-testid="location"
          primary={location?.name || <LineIcon width="24px" />}
          title={location?.name}
          primaryTextProps={{
            textDecoration: contribution.isExcluded ? 'line-through' : 'none',
          }}
        />
      </Td>
      {[ReleaseType.LABEL].includes(releaseType) && (
        <Td>
          <ListItem data-testid="credited">
            {!isUpdating[contribution.contributionGuid] ? (
              <>
                <Checkbox
                  isChecked={contribution.isCredited}
                  onChange={() => onCreditedChange(contribution)}
                  isDisabled={contribution.isExcluded}
                />
                {contribution.isExcluded && (
                  <chakra.span
                    position="absolute"
                    width="40px"
                    height="1px"
                    left={-2}
                  />
                )}
              </>
            ) : (
              <Spinner data-testid="isCredited-loading" size="md" />
            )}
          </ListItem>
        </Td>
      )}
      <Td>
        {errorMsg ? (
          <ValidationTooltip errorMsg={errorMsg} tab="contributions" />
        ) : (
          ''
        )}
      </Td>
      <Td />
      <Td>
        <ContextMenuButton onClick={contextHandler} />
      </Td>
    </Tr>
  );
};
